<template>
  <div>
    <div v-if="isImageFile && fileUrl" @click.stop>
      <image-modal
        :image-url="fullImageUrl"
        :alt="$t('common.thumbnail-alt')"
        show-download-button
      >
        <div :class="imageContainerClass">
          <image-view
            :src="currentImageSrc"
            :cover-img="true"
            without-padding
            :shape="ImageShape.ROUNDED"
            :alt="$t('common.thumbnail-alt')"
            class="cursor-pointer"
            @error="handleImageError"
          />
        </div>
      </image-modal>
    </div>

    <div v-else-if="!isImageFile && fileUrl && hasValidExtension" @click.stop>
      <a
        :href="fileUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-block cursor-pointer"
      >
        <div
          :class="[
            size === 'large' ? 'h-20 w-20' : 'h-16 w-16',
            'bg-blue-lighter rounded border-2 border-blue flex items-center justify-center',
          ]"
        >
          <icon
            :icon="mdiFileDocumentOutline"
            :size="IconSize.XL"
            class="text-blue"
          />
        </div>
      </a>
    </div>

    <div v-else-if="fileUrl && !hasValidExtension">
      <div
        :class="[
          size === 'large' ? 'h-20 w-20' : 'h-16 w-16',
          'bg-gray-lighter rounded border-2 border-gray flex items-center justify-center opacity-50 cursor-not-allowed',
        ]"
      >
        <icon
          :icon="mdiFileQuestionOutline"
          :size="IconSize.XL"
          class="text-gray-darker"
        />
      </div>
    </div>

    <span v-else-if="!canEdit">
      {{ $t('brand-surveys.answers.no-answer') }}
    </span>
  </div>
</template>

<script>
import ImageModal from '@/components/Modal/ImageModal.vue';
import ImageView from '@/components/ImageView/ImageView.vue';
import { ImageShape } from '@/components/ImageView/image.constants';
import { Icon, IconSize } from '@/components/Icon';
import { mdiFileDocumentOutline, mdiFileQuestionOutline } from '@mdi/js';

export default {
  name: 'SurveyFileAnswer',
  components: {
    ImageModal,
    ImageView,
    Icon,
  },
  props: {
    fileUrl: {
      type: String,
      default: '',
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    showFullResolution: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ImageShape,
      IconSize,
      mdiFileDocumentOutline,
      mdiFileQuestionOutline,
      imageLoadError: false,
    };
  },
  computed: {
    hasValidExtension() {
      if (!this.fileUrl) return false;

      try {
        const url = new URL(this.fileUrl);
        const ext = url.searchParams.get('ext');
        return !!ext;
      } catch {
        return false;
      }
    },

    isImageFile() {
      if (!this.fileUrl || !this.hasValidExtension) return false;

      const url = new URL(this.fileUrl);
      const ext = url.searchParams.get('ext');

      const imageExtensions = [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'bmp',
        'webp',
        'svg',
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.bmp',
        '.webp',
        '.svg',
      ];
      return imageExtensions.includes(ext.toLowerCase());
    },

    thumbnailUrl() {
      if (!this.fileUrl || !this.isImageFile) return this.fileUrl;

      try {
        const url = new URL(this.fileUrl);
        url.searchParams.set('showThumbnail', 'true');
        return url.toString();
      } catch {
        return this.fileUrl;
      }
    },

    fullImageUrl() {
      if (!this.fileUrl) return '';

      try {
        const url = new URL(this.fileUrl);
        url.searchParams.delete('showThumbnail');
        return url.toString();
      } catch {
        return this.fileUrl;
      }
    },

    currentImageSrc() {
      if (!this.isImageFile) {
        return this.fileUrl;
      }
      if (this.canEdit) {
        return this.fileUrl;
      }
      if (this.imageLoadError) {
        return this.fileUrl;
      }
      if (this.showFullResolution) {
        return this.fileUrl;
      }
      return this.thumbnailUrl;
    },

    imageContainerClass() {
      if (this.fullWidth) {
        return 'w-full h-48';
      }
      return this.size === 'large' ? 'h-20 w-20' : 'h-16 w-16';
    },
  },
  watch: {
    fileUrl: {
      immediate: true,
      handler() {
        this.imageLoadError = false;
      },
    },
  },
  methods: {
    handleImageError() {
      if (!this.imageLoadError && !this.canEdit) {
        this.imageLoadError = true;
      }
    },
  },
};
</script>
